import React, { useState, useEffect } from 'react';
import { Activity, DollarSign, TrendingUp, TrendingDown, Calendar, Download, Filter, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Card } from '../../../components/ui/card';
import { useToast } from '../../../components/ui/use-toast';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CashFlowReport = () => {
  const { toast } = useToast();
  
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [openingBalance, setOpeningBalance] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setEndDate(format(today, 'yyyy-MM-dd'));
    setStartDate(format(thirtyDaysAgo, 'yyyy-MM-dd'));
  }, []);
  
  const fetchReport = async () => {
    if (!startDate || !endDate) {
      toast({
        variant: "destructive",
        title: "Missing Dates",
        description: "Please select start and end dates",
      });
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      let url = `${API_URL}/api/finance/reports/cashflow?start_date=${startDate}&end_date=${endDate}&currency=${currency}`;
      
      if (openingBalance) {
        url += `&opening_balance=${openingBalance}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportData(response.data);
      
      toast({
        title: "Report Generated",
        description: "Cash flow report generated successfully",
      });
    } catch (error) {
      console.error('Error fetching cash flow report:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to generate report",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(value);
  };
  
  const formatChartData = () => {
    if (!reportData?.monthly_breakdown) return [];
    return reportData.monthly_breakdown.map(item => ({
      month: item.month,
      inflow: parseFloat(item.inflow),
      outflow: parseFloat(item.outflow),
      net: parseFloat(item.net_flow)
    }));
  };
  
  const handleExport = async (format) => {
    if (!reportData) {
      toast({
        variant: "destructive",
        title: "No Report Data",
        description: "Please generate a report first",
      });
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      let url = `${API_URL}/api/finance/reports/cashflow/export/${format}?start_date=${startDate}&end_date=${endDate}&currency=${currency}`;
      
      if (openingBalance) {
        url += `&opening_balance=${openingBalance}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const extension = format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'csv';
      link.setAttribute('download', `cashflow_report_${startDate}_${endDate}.${extension}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "Export Successful",
        description: `Cash flow report exported as ${format.toUpperCase()}`,
      });
    } catch (error) {
      console.error(`Error exporting to ${format}:`, error);
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: error.response?.data?.detail || `Failed to export report as ${format}`,
      });
    }
  };
  
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#2B2D42] flex items-center gap-3">
          <Activity className="w-8 h-8 text-[#00B4D8]" />
          Cash Flow Report
        </h1>
        <p className="text-[#6C757D] mt-2">
          Cash inflow, outflow, and runway analysis
        </p>
      </div>
      
      {/* Filters */}
      <Card className="p-6 mb-6 border-[#E9ECEF]">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-[#6C757D]" />
          <h2 className="text-lg font-semibold text-[#2B2D42]">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Start Date
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              End Date
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Currency
            </label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="BRL">BRL</SelectItem>
                <SelectItem value="JPY">JPY</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Opening Balance
            </label>
            <Input
              type="number"
              value={openingBalance}
              onChange={(e) => setOpeningBalance(e.target.value)}
              placeholder="0.00"
              className="w-full"
            />
          </div>
          
          <div className="flex items-end">
            <Button
              onClick={fetchReport}
              disabled={loading}
              className="w-full bg-[#00B4D8] hover:bg-[#0096c7] text-white"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Generate Report
                </div>
              )}
            </Button>
          </div>
        </div>
      </Card>
      
      {reportData && (
        <>
          {/* Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Opening Balance</span>
                <DollarSign className="w-5 h-5 text-[#6C757D]" />
              </div>
              <p className="text-2xl font-bold text-[#2B2D42]">
                {formatCurrency(reportData.metrics.opening_balance)}
              </p>
            </Card>
            
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Cash Inflow</span>
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(reportData.metrics.cash_inflow)}
              </p>
            </Card>
            
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Cash Outflow</span>
                <TrendingDown className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(reportData.metrics.cash_outflow)}
              </p>
            </Card>
            
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Net Cash Flow</span>
                <Activity className="w-5 h-5 text-[#00B4D8]" />
              </div>
              <p className={`text-2xl font-bold ${parseFloat(reportData.metrics.net_cash_flow) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(reportData.metrics.net_cash_flow)}
              </p>
            </Card>
            
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Closing Balance</span>
                <DollarSign className="w-5 h-5 text-[#00B4D8]" />
              </div>
              <p className={`text-2xl font-bold ${parseFloat(reportData.metrics.closing_balance) >= 0 ? 'text-[#2B2D42]' : 'text-red-600'}`}>
                {formatCurrency(reportData.metrics.closing_balance)}
              </p>
            </Card>
          </div>
          
          {/* Burn Rate & Runway */}
          {reportData.metrics.burn_rate && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Card className="p-6 border-[#E9ECEF]">
                <h3 className="text-sm font-medium text-[#6C757D] mb-2">Monthly Burn Rate</h3>
                <p className="text-3xl font-bold text-[#2B2D42]">
                  {formatCurrency(reportData.metrics.burn_rate)}
                </p>
                <p className="text-sm text-[#6C757D] mt-2">Average monthly expenses</p>
              </Card>
              
              <Card className={`p-6 border-[#E9ECEF] ${reportData.metrics.runway_months && reportData.metrics.runway_months < 6 ? 'bg-red-50 border-red-200' : ''}`}>
                <h3 className="text-sm font-medium text-[#6C757D] mb-2 flex items-center gap-2">
                  Runway
                  {reportData.metrics.runway_months && reportData.metrics.runway_months < 6 && (
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  )}
                </h3>
                <p className={`text-3xl font-bold ${reportData.metrics.runway_months && reportData.metrics.runway_months < 6 ? 'text-red-600' : 'text-[#2B2D42]'}`}>
                  {reportData.metrics.runway_months ? `${reportData.metrics.runway_months.toFixed(1)} months` : 'N/A'}
                </p>
                <p className="text-sm text-[#6C757D] mt-2">
                  {reportData.metrics.runway_months && reportData.metrics.runway_months < 6 
                    ? '⚠️ Low runway - consider increasing revenue or reducing costs'
                    : 'Months until balance reaches $0'
                  }
                </p>
              </Card>
            </div>
          )}
          
          {/* Cash Flow Chart */}
          {reportData.monthly_breakdown && reportData.monthly_breakdown.length > 0 && (
            <Card className="p-6 mb-6 border-[#E9ECEF]">
              <h3 className="text-lg font-semibold text-[#2B2D42] mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#00B4D8]" />
                Cash Flow Over Time
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={formatChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#6C757D"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#6C757D"
                    style={{ fontSize: '12px' }}
                    tickFormatter={(value) => `${currency} ${value.toLocaleString()}`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E9ECEF',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="inflow" 
                    stackId="1"
                    stroke="#10B981" 
                    fill="#10B981"
                    fillOpacity={0.6}
                    name="Cash Inflow"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="outflow" 
                    stackId="2"
                    stroke="#EF4444" 
                    fill="#EF4444"
                    fillOpacity={0.6}
                    name="Cash Outflow"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          )}
          
          {/* Forecast */}
          {reportData.forecast_next_3_months && reportData.forecast_next_3_months.length > 0 && (
            <Card className="p-6 mb-6 border-[#E9ECEF] bg-blue-50">
              <h3 className="text-lg font-semibold text-[#2B2D42] mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#00B4D8]" />
                3-Month Forecast (Based on Historical Average)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-blue-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-[#2B2D42]">Month</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-[#2B2D42]">Forecasted Inflow</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-[#2B2D42]">Forecasted Outflow</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-[#2B2D42]">Net Flow</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-[#2B2D42]">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.forecast_next_3_months.map((forecast) => (
                      <tr key={forecast.month} className="border-b border-blue-200">
                        <td className="py-3 px-4 font-medium text-[#2B2D42]">{forecast.month}</td>
                        <td className="py-3 px-4 text-right text-green-600 font-semibold">
                          {formatCurrency(forecast.forecasted_inflow)}
                        </td>
                        <td className="py-3 px-4 text-right text-red-600 font-semibold">
                          {formatCurrency(forecast.forecasted_outflow)}
                        </td>
                        <td className={`py-3 px-4 text-right font-semibold ${forecast.forecasted_net_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(forecast.forecasted_net_flow)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="px-2 py-1 bg-blue-200 text-blue-800 rounded-full text-xs font-medium">
                            {forecast.confidence}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-[#6C757D] mt-4">
                ℹ️ Forecast based on 3-month historical average. Actual results may vary.
              </p>
            </Card>
          )}
          
          {/* Export */}
          <Card className="p-6 border-[#E9ECEF]">
            <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">Export Report</h3>
            <div className="flex gap-3">
              <Button variant="outline" className="flex items-center gap-2" onClick={() => handleExport('pdf')}>
                <Download className="w-4 h-4" />
                Export PDF
              </Button>
              <Button variant="outline" className="flex items-center gap-2" onClick={() => handleExport('excel')}>
                <Download className="w-4 h-4" />
                Export Excel
              </Button>
              <Button variant="outline" className="flex items-center gap-2" onClick={() => handleExport('csv')}>
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </Card>
        </>
      )}
      
      {!reportData && !loading && (
        <Card className="p-12 text-center border-[#E9ECEF]">
          <Activity className="w-16 h-16 text-[#6C757D] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#2B2D42] mb-2">
            No Report Generated
          </h3>
          <p className="text-[#6C757D] mb-6">
            Select dates and click "Generate Report" to view cash flow analysis
          </p>
        </Card>
      )}
    </div>
  );
};

export default CashFlowReport;
